import {
	useGetUpcomingEventsForUser,
	useGetPastEventsForUser,
	useGetGeneralEventFrontEndClientSettings,
} from '../../../FrontEndReact/src/Core/Services/event';
import ObjectMiniList, { ListObject, ObjectType } from '../../Components/ObjectMiniList';
import { Event, EventListFilter, EventStatus, ParticipationStatus } from '../../../FrontEndReact/src/Core/Types/event';
import { formatDate } from '../../../FrontEndReact/src/Core/Utils/dateUtils';
import { isEventRecruitingSubstitutes } from '../../../FrontEndReact/src/Core/Utils/eventRecruitment';
import './EventList.scss';
import EventMonthCalendar from '../../../FrontEndReact/src/Core/Components/Event/EventMonthCalendar';
import { goto } from '../../../Js/React/Services/legacy';
//import useWindowSize from '../../../FrontEndReact/src/Core/Hooks/useWindowSize';
import BizPhrase from '../../Components/Phrase';
import { useFrontendState } from '../../Common/frontendStateContext';
import BizTabs from '../../../FrontEndReact/src/Core/Components/Mini/BizTabs';
import { useSessionPreference } from '../../../FrontEndReact/src/Core/Hooks/useSessionPreference';
import { useGetTranslations } from '../../../Js/React/Services/legacy';
//import { goto } from '../../../Js/React/Services/legacy';

const EVENT_LIST_FILTER_KEYS = ['all', 'upcoming', 'past', 'mine', 'missing-coorg', 'calendar'] as const;
const EVENT_LIST_FILTER_TRANSLATION_KEYS = [
	'EVENTLIST.ALL',
	'EVENTLIST.CALENDAR',
	'STAFFING_CALENDAR.LOADING',
	'EVENTLIST.UPCOMING',
	'EVENTLIST.PAST',
	'EVENTLIST.MINE',
	'EVENTLIST.MISSING_COORGANIZER',
	'EVENTLIST.NO_DATA_TITLE',
	'EVENTLIST.NO_DATA_TEXT',
];

type EventListTabFilter = (typeof EVENT_LIST_FILTER_KEYS)[number];

const normalizeEventListFilters = (filters?: string[]): EventListTabFilter[] => {
	const normalizedFilters = (filters ?? []).map((filter) =>
		filter === 'missing-coorganizer' ? 'missing-coorg' : filter.toLowerCase()
	);

	return normalizedFilters.filter(
		(filter, index): filter is EventListTabFilter =>
			EVENT_LIST_FILTER_KEYS.includes(filter as EventListTabFilter) && normalizedFilters.indexOf(filter) === index
	);
};

type EventListProps = {
	eventFilter?: EventListFilter;
	desktopLayout?: string;
	mobileLayout?: string;
	limit?: number;
	itemsPerRow?: number;
	labelStyle?: string;
	filters?: string[];
};

const EventList = ({ eventFilter, desktopLayout, mobileLayout, limit, itemsPerRow, labelStyle, filters }: EventListProps) => {
	const configuredFilters = normalizeEventListFilters(filters);
	const defaultFilter = eventFilter === 'past' && configuredFilters.includes('past') ? 'past' : configuredFilters[0];
	const preferenceKey = `biz-event-list:${window.location.pathname}:${eventFilter ?? ''}:${configuredFilters.join(',')}`;
	const [selectedFilter, setSelectedFilter] = useSessionPreference<EventListTabFilter>(
		`${preferenceKey}:filter`,
		defaultFilter ?? 'upcoming',
		(value) => configuredFilters.includes(value as EventListTabFilter)
	);
	const shouldLoadPastEvents = configuredFilters.length > 0 ? configuredFilters.includes('past') : eventFilter === 'past';
	const shouldLoadUpcomingEvents =
		configuredFilters.length > 0 ? configuredFilters.some((filter) => filter !== 'past') : eventFilter !== 'past';
	const upcomingEvents = useGetUpcomingEventsForUser(shouldLoadUpcomingEvents);
	const pastEvents = useGetPastEventsForUser(shouldLoadPastEvents);

	const { state, getCustomFrontEndConfig } = useFrontendState();
	const settings = useGetGeneralEventFrontEndClientSettings();
	const customConfig = getCustomFrontEndConfig('event');
	const translations = useGetTranslations(EVENT_LIST_FILTER_TRANSLATION_KEYS);
	const usesPastEvents = configuredFilters.length > 0 ? selectedFilter === 'past' : eventFilter === 'past';
	let events = usesPastEvents ? pastEvents : upcomingEvents;
	console.log('eventlist render');
	//const { isMobile } = useWindowSize();

	console.log('events', events);

	if (!events && selectedFilter !== 'calendar')
		return <p role='status'>{translations['STAFFING_CALENDAR.LOADING'] || 'Laddar...'}</p>;
	events = events ?? [];

	if (!desktopLayout) desktopLayout = 'hero';
	if (!mobileLayout) mobileLayout = 'carousel';
	if (!labelStyle) labelStyle = 'default';

	const canHandleEvents = [...(upcomingEvents ?? []), ...(pastEvents ?? [])].some((event) => event.IsAllowedToHandleEvent);
	const userId = state.user?.Id;
	const filterTabs = configuredFilters
		.filter((filter) => filter !== 'mine' || canHandleEvents)
		.filter(
			(filter) => filter !== 'missing-coorg' || (canHandleEvents && settings?.AllowSelfAddSubstituteCoOrganizers === true)
		)
		.map((filter) => ({
			key: filter,
			title:
				filter === 'calendar'
					? translations['EVENTLIST.CALENDAR'] || 'Kalender'
					: filter === 'all'
						? translations['EVENTLIST.ALL'] || 'All'
						: filter === 'upcoming'
							? translations['EVENTLIST.UPCOMING'] || 'Upcoming'
							: filter === 'past'
								? translations['EVENTLIST.PAST'] || 'Past'
								: filter === 'mine'
									? translations['EVENTLIST.MINE'] || 'Mine'
									: translations['EVENTLIST.MISSING_COORGANIZER'] || 'Missing co-organizer',
		}));
	const visibleSelectedFilter = filterTabs.some((tab) => tab.key === selectedFilter)
		? selectedFilter
		: (filterTabs[0]?.key ?? (selectedFilter === 'calendar' ? 'upcoming' : selectedFilter));
	const eventFilterTabs =
		eventFilter !== 'missing-coorganizer' && filterTabs.length > 1 ? (
			<div className='user-event-list-filter'>
				<BizTabs
					tabs={filterTabs}
					selectedTabKey={visibleSelectedFilter}
					appearance='rounded'
					onClick={(key) => setSelectedFilter(key as EventListTabFilter)}
					ariaLabel='Filter events'
				/>
			</div>
		) : null;
	const noDataMessage = (
		<div className='user-event-list-empty'>
			<strong>{translations['EVENTLIST.NO_DATA_TITLE'] || 'No events right now'}</strong>
			<p>{translations['EVENTLIST.NO_DATA_TEXT'] || 'Your events will appear here.'}</p>
		</div>
	);

	if (configuredFilters.length > 0 && canHandleEvents) {
		if (visibleSelectedFilter === 'mine') {
			events = events.filter(
				(event) => event.Organizer?.Id === userId || event.CoOrganizers?.some((coOrganizer) => coOrganizer.Id === userId)
			);
		} else if (visibleSelectedFilter === 'missing-coorg') {
			events = events.filter((event) => isEventRecruitingSubstitutes(event, settings));
		}
	}

	// const goToEvent = (eventId: number) => {
	// 	goto('base.modules.event', { id: eventId });
	// };

	const mapToListObject = (event: Event): ListObject => {
		// A bit customer specific logic for AHT, only show desc. if whe have eventFilter
		const desc =
			window.portalAlias === 'AHealthierTomorrow' && !event.IsAllowedToHandleEvent
				? (event.Description ?? '')
				: (event?.ShortDescription ?? '');
		const fullDesc = (event.Location ?? '') + (eventFilter ? (desc.length > 0 ? ' - ' : '') + desc : '');

		const getBanner = (): { title: string; text: string; className: string } | undefined => {
			if (event.Status === EventStatus.Cancelled) return { title: '', text: 'Inställt!', className: 'cancelled' };
			if (isEventRecruitingSubstitutes(event, settings))
				return { title: 'Tillgängligt pass.', text: 'Inhoppare behövs!', className: 'substitute-needed' };

			return undefined;
		};

		const banner = getBanner();

		return {
			Id: event.Id,
			Name: event.Name,
			Description: fullDesc,
			ImageUrl: event.ImageUrl ?? '',
			EngageObjects: [],
			Url: { state: 'base.modules.event', params: { id: event.Id } },
			OverrideTypeLabels: [
				{
					type: 'EVENT' as ObjectType,
					overrideLabels:
						labelStyle === 'large'
							? [
									formatDate(event.StartDateTimeUtc, 'd'),
									formatDate(event.StartDateTimeUtc, 'MMM').replace('.', ''),
								]
							: [formatDate(event.StartDateTimeUtc, 'd MMMM')],
					overrideIcon: '',
				},
				...(event.UserStatus === ParticipationStatus.Attending ||
				event.UserStatus === ParticipationStatus.ConfirmedAttended
					? [{ type: 'EVENT' as ObjectType, overrideLabels: ['Anmäld'], overrideIcon: '' }]
					: []),
			],
			BannerTitle: banner?.title,
			BannerText: banner?.text,
			BannerClass: banner?.className,
		};
	};

	if (visibleSelectedFilter === 'calendar') {
		return (
			<div className='user-event-list'>
				{eventFilterTabs}
				<EventMonthCalendar
					staffingEnabled={settings?.IsEventStaffingEnabled === true}
					listEvents={upcomingEvents}
					loading={!upcomingEvents}
					preferenceKey={`${preferenceKey}:month`}
					onOpen={(id) => goto('base.modules.event', { id })}
				/>
			</div>
		);
	}

	if (!eventFilter) {
		//isMobile &&
		const noRsvpEvents = events.filter((e) => e.DoNotRequireRSVP);
		const rsvpEvents = events.filter((e) => !e.DoNotRequireRSVP);

		const rsvpListObjects: ListObject[] = rsvpEvents.map(mapToListObject);
		const noRsvpListObjects: ListObject[] = noRsvpEvents.map(mapToListObject);

		return (
			<div className='user-event-list'>
				{eventFilterTabs}
				{rsvpListObjects.length === 0 && noRsvpListObjects.length === 0 && configuredFilters.length > 0 && noDataMessage}
				{rsvpListObjects.length > 0 && (
					<div className='user-event-list-rsvp-events'>
						<h2 className='biz-sub-header'>
							<BizPhrase phraseKey='EVENTLIST.RSVP_LIST.HEADER'></BizPhrase>
						</h2>
						<ObjectMiniList
							objects={rsvpListObjects}
							layoutMobile={mobileLayout}
							layout={desktopLayout}
							type='EVENT'
							limit={limit}
							itemsPerRow={itemsPerRow}
							labelStyle={labelStyle}
						/>
					</div>
				)}
				{noRsvpListObjects.length > 0 && (
					<div className='user-event-list-norsvp-events'>
						<h2 className='biz-sub-header'>
							<BizPhrase phraseKey='EVENTLIST.NO_RSVP_LIST.HEADER'></BizPhrase>
						</h2>
						<ObjectMiniList
							objects={noRsvpListObjects}
							layoutMobile={mobileLayout}
							layout={desktopLayout}
							type='EVENT'
							limit={limit}
							itemsPerRow={itemsPerRow}
							labelStyle={labelStyle}
						/>
					</div>
				)}
			</div>
		);
	}

	if (eventFilter) {
		if (eventFilter === 'missing-coorganizer' || eventFilter === 'missing-coorg') {
			events = events.filter((event) => isEventRecruitingSubstitutes(event, settings));
		}
		if (customConfig?.eventListFilterFunction) {
			events = customConfig.eventListFilterFunction(eventFilter, events);
		} else {
			if (eventFilter === 'open') {
				events = events.filter((e) => e.DoNotRequireRSVP);
			} else if (eventFilter === 'rsvp') {
				events = events.filter((e) => !e.DoNotRequireRSVP);
			}
		}
	}

	const listObjects: ListObject[] = events.map(mapToListObject);

	//temp until we make a better generic list module

	return (
		<div className='user-event-list'>
			{eventFilterTabs}
			{listObjects.length === 0 && (configuredFilters.length > 0 || !!eventFilter) ? (
				noDataMessage
			) : (
				<ObjectMiniList
					objects={listObjects}
					type='EVENT'
					limit={limit}
					itemsPerRow={itemsPerRow}
					labelStyle={labelStyle}
					layoutMobile={mobileLayout}
					layout={desktopLayout}
				/>
			)}
		</div>
	);
};

export default EventList;
